/**
 * Settings -> Notifications -> "Do not email": stop the email channel for ONE named person.
 *
 * WHY THIS EXISTS. Email settings are otherwise per company, per module and per channel
 * (`notification-settings.contract.ts`), so the only way to stop email to one person today is to
 * switch a module off for everyone, or to deactivate the person. Neither is right when someone has
 * asked to be left alone, an address is bouncing, or an account is a test fixture. This is the
 * missing per-person switch, and it stays HR/ADMIN ONLY like the rest of the notification config:
 * the settled product decision is that employees configure nothing for themselves.
 *
 * WHAT A MUTE DOES (implemented in the follow-up PR, stated here so the contract has one meaning):
 *   - `notify()` skips the `email` channel for this person and records the delivery as `suppressed`
 *     with `EMAIL_MUTED_REASON`, so Delivery Activity answers "why didn't it go out" the usual way.
 *   - Every other channel (in-app, push, SMS, WhatsApp) is untouched.
 *   - It is stored per person and per company, and read inside `withTenant()` like any other row.
 *
 * WHAT A MUTE DOES NOT DO — OPEN PRODUCT QUESTION, DEFAULT CHOSEN HERE:
 *   Mandatory notices (statutory and policy notices, payslip availability) and account emails
 *   (invitation, verification, password reset) are NOT muted. `preferences.ts` explains why HR may
 *   not mute the first group: "we sent it, but we had muted it" is no defence in a labour dispute.
 *   The second group is the person's only way in. If the owner wants a mute to cover those too, it
 *   is an explicit opt-in on the request, not a silent default; nothing in this contract allows it.
 *
 * `pathPrefix` and `routeMeta` follow `notification-settings.contract.ts`, whose `notification.config:*`
 * permissions gate these routes too. Deliberately a separate contract: that router is typed
 * exhaustively against its contract, so adding routes to it would force handlers into this PR and
 * break "contract in its own PR first" (CLAUDE.md, invariant 8).
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { routeMeta } from './meta.js';
import {
  errorBodySchema,
  instantSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

/**
 * `NotificationDelivery.reason` for an email a mute suppressed. One shared string so the sender,
 * Delivery Activity and the platform panel all say the same thing.
 */
export const EMAIL_MUTED_REASON = 'email muted for this person';

export const EMAIL_MUTE_REASON_MAX_LENGTH = 500;

export const emailMuteItemSchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  /** The work email the person is known by; shown so an admin can confirm who this is. */
  email: z.string().nullable(),
  /** Why, in the admin's words. Never shown to the person. Null when none was given. */
  reason: z.string().nullable(),
  mutedBy: uuidSchema.nullable(),
  mutedByName: z.string().nullable(),
  mutedAt: instantSchema,
});
export type EmailMuteItem = z.infer<typeof emailMuteItemSchema>;

export const listEmailMutesQuerySchema = paginationQuerySchema.extend({
  /** Matches the person's name or email. */
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListEmailMutesQuery = z.infer<typeof listEmailMutesQuerySchema>;

export const emailMutePersonParamsSchema = z.object({ personId: uuidSchema });

export const muteEmailBodySchema = z.object({
  reason: z.string().trim().min(1).max(EMAIL_MUTE_REASON_MAX_LENGTH).optional(),
});
export type MuteEmailBody = z.infer<typeof muteEmailBodySchema>;

export const unmuteEmailResponseSchema = z.object({
  personId: uuidSchema,
  muted: z.literal(false),
});
export type UnmuteEmailResponse = z.infer<typeof unmuteEmailResponseSchema>;

export const notificationEmailMuteContract = c.router(
  {
    listEmailMutes: {
      method: 'GET',
      path: '/email-mutes',
      query: listEmailMutesQuerySchema,
      responses: {
        200: paginatedSchema(emailMuteItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'People whose email is switched off, newest first',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    muteEmail: {
      method: 'PUT',
      path: '/email-mutes/:personId',
      pathParams: emailMutePersonParamsSchema,
      body: muteEmailBodySchema,
      responses: {
        200: emailMuteItemSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Stop the email channel for one person. Idempotent: muting someone already muted updates the reason',
      metadata: routeMeta('notification.config:manage'),
    },
    unmuteEmail: {
      method: 'DELETE',
      path: '/email-mutes/:personId',
      pathParams: emailMutePersonParamsSchema,
      body: c.noBody(),
      responses: {
        200: unmuteEmailResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Let email reach this person again. Idempotent: unmuting someone not muted is a 200; 404 only when the person is not in this company',
      metadata: routeMeta('notification.config:manage'),
    },
  },
  { pathPrefix: '/notification-settings' },
);
